import React, { useMemo } from 'react';
import { Lead } from '../../types';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import { Calendar, Clock, Flame } from 'lucide-react';
import './LeadWebTraffic.css';

interface LeadWebTrafficProps {
  lead: Lead;
}

const LeadWebTraffic: React.FC<LeadWebTrafficProps> = ({ lead }) => {
  const recentEvents = lead.recentEvents || [];
  const statistics = lead.statistics;
  const trackingData = lead.trackingData;

  // Calculate sales-focused metrics
  const metrics = useMemo(() => {
    // Early return if no Metrics Lite ID
    if (!lead.metricsLiteId) {
      return {
        topPages: [],
        visitsLastTwoWeeks: 0,
        lastVisitDate: null,
        lastVisitStatus: 'inactive' as const,
        lastVisitText: 'No activity',
        engagementLevel: 'none' as const,
        pageviewsCount: 0,
        goalCompletions: 0,
      };
    }
    // Top Pages Calculation
    const pageViews: Record<string, { count: number; path: string }> = {};
    recentEvents.forEach(event => {
      if (event.type === 'pageview' && event.url) {
        try {
          const urlObj = new URL(event.url);
          const path = urlObj.pathname;
          const key = path || '/';
          if (!pageViews[key]) {
            pageViews[key] = { count: 0, path: key === '/' ? 'Home' : path };
          }
          pageViews[key].count++;
        } catch {
          // If URL parsing fails, use the full URL
          const key = event.url;
          if (!pageViews[key]) {
            pageViews[key] = { count: 0, path: event.url.length > 30 ? event.url.substring(0, 30) + '...' : event.url };
          }
          pageViews[key].count++;
        }
      }
    });

    const topPages = Object.entries(pageViews)
      .map(([key, data]) => ({
        path: data.path,
        count: data.count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3); // Top 3 pages

    // Visit Frequency (last 2 weeks)
    const twoWeeksAgo = subDays(new Date(), 14);
    const uniqueVisitDates = new Set<string>();
    recentEvents.forEach(event => {
      if (event.createdAt) {
        const eventDate = event.createdAt instanceof Date 
          ? event.createdAt 
          : new Date(event.createdAt);
        if (!isNaN(eventDate.getTime()) && eventDate >= twoWeeksAgo) {
          const dateKey = format(eventDate, 'yyyy-MM-dd');
          uniqueVisitDates.add(dateKey);
        }
      }
    });

    // Last Visit Time
    let lastVisitDate: Date | null = null;
    if (trackingData?.lastSeen) {
      const date = trackingData.lastSeen instanceof Date 
        ? trackingData.lastSeen 
        : new Date(trackingData.lastSeen);
      if (!isNaN(date.getTime())) {
        lastVisitDate = date;
      }
    }
    
    // Fallback to most recent event if no lastSeen
    if (!lastVisitDate && recentEvents.length > 0) {
      const sortedEvents = [...recentEvents]
        .filter(e => e.createdAt)
        .map(e => ({
          ...e,
          createdAt: e.createdAt instanceof Date ? e.createdAt : new Date(e.createdAt),
        }))
        .filter(e => !isNaN(e.createdAt.getTime()))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      if (sortedEvents.length > 0) {
        lastVisitDate = sortedEvents[0].createdAt;
      }
    }

    // Calculate time since last visit
    let lastVisitStatus: 'active' | 'recent' | 'inactive' = 'inactive';
    let lastVisitText = 'No activity';
    if (lastVisitDate) {
      const hoursSince = (Date.now() - lastVisitDate.getTime()) / (1000 * 60 * 60);
      if (hoursSince < 24) {
        lastVisitStatus = 'active';
        lastVisitText = formatDistanceToNow(lastVisitDate, { addSuffix: true });
      } else if (hoursSince < 168) { // 7 days
        lastVisitStatus = 'recent';
        lastVisitText = formatDistanceToNow(lastVisitDate, { addSuffix: true });
      } else {
        lastVisitStatus = 'inactive';
        lastVisitText = formatDistanceToNow(lastVisitDate, { addSuffix: true });
      }
    }

    // Engagement Score
    const visitsCount = uniqueVisitDates.size;
    const pageviewsCount = statistics?.pageviews || 0;
    const goalCompletions = statistics?.goalCompletions || 0;
    
    let engagementLevel: 'high' | 'medium' | 'low' | 'none' = 'none';
    if (visitsCount >= 5 || pageviewsCount >= 20 || goalCompletions > 0) {
      engagementLevel = 'high';
    } else if (visitsCount >= 2 || pageviewsCount >= 5) {
      engagementLevel = 'medium';
    } else if (visitsCount >= 1 || pageviewsCount >= 1) {
      engagementLevel = 'low';
    }

    return {
      topPages,
      visitsLastTwoWeeks: uniqueVisitDates.size,
      lastVisitDate,
      lastVisitStatus,
      lastVisitText,
      engagementLevel,
      pageviewsCount,
      goalCompletions,
    };
  }, [lead.metricsLiteId, recentEvents, statistics, trackingData]);

  // Only render if Metrics Lite ID exists
  if (!lead.metricsLiteId) {
    return null;
  }

  // Don't render if no data available
  if (metrics.topPages.length === 0 && metrics.visitsLastTwoWeeks === 0 && !metrics.lastVisitDate) {
    return (
      <div className="lead-web-traffic">
        <div className="web-traffic-empty">No web activity data</div>
      </div>
    );
  }

  return (
    <div className="lead-web-traffic">
      {/* Top Pages */}
      {metrics.topPages.length > 0 && (
        <div className="web-traffic-section">
          <div className="web-traffic-label">Top Pages:</div>
          <div className="web-traffic-pages">
            {metrics.topPages.map((page, index) => (
              <span key={index} className="page-badge">
                {page.path} <span className="page-count">({page.count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Visit Frequency & Last Visit */}
      <div className="web-traffic-section">
        <div className="web-traffic-metrics">
          {metrics.visitsLastTwoWeeks > 0 && (
            <span className="metric-badge visits">
              <span className="metric-icon"><Calendar size={14} /></span>
              {metrics.visitsLastTwoWeeks} visit{metrics.visitsLastTwoWeeks !== 1 ? 's' : ''}
            </span>
          )}
          {metrics.lastVisitDate && (
            <span className={`metric-badge last-visit ${metrics.lastVisitStatus}`}>
              <span className="metric-icon"><Clock size={14} /></span>
              {metrics.lastVisitText}
            </span>
          )}
          {metrics.engagementLevel !== 'none' && (
            <span className={`metric-badge engagement ${metrics.engagementLevel}`}>
              <span className="metric-icon"><Flame size={14} /></span>
              {metrics.engagementLevel.charAt(0).toUpperCase() + metrics.engagementLevel.slice(1)} engagement
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadWebTraffic;
